import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Image,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  Heading,
  Badge,
  useColorModeValue,
  Flex,
  SimpleGrid,
  Link,
  Progress,
  Stack,
  Divider,
  useColorMode,
  InputGroup,
  InputLeftElement,
  Input,
  Skeleton,
  InputRightElement,
} from "@chakra-ui/react";
import { useCart } from "../context/CartContext";
import { Link as RouterLink } from "react-router-dom";
import {
  FiSun,
  FiStar,
  FiShield,
  FiTruck,
  FiLock,
  FiCheckCircle,
  FiClock,
  FiPercent,
  FiShoppingBag,
  FiTrendingUp,
  FiZap,
  FiBattery,
  FiTarget,
  FiAward,
  FiSearch,
  FiTag,
  FiGrid,
  FiArrowRight,
  FiCheck,
  FiShoppingCart,
} from "react-icons/fi";
import { 
  FaMagic, 
  FaLeaf,
  FaGem,
  FaRegClock,
  FaSprayCan,
  FaCertificate,
  FaShippingFast,
  FaTree,
  FaWind,
  FaCookie,
} from "react-icons/fa";
import type { Product } from "../types";
import { products } from "../data/products";
import Hero from "../components/HeroFlashlight";
import { motion } from "framer-motion";
import { categories } from "../data/categories";

const MotionBox = motion(Box);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const Home: React.FC = () => {
  const { addToCart } = useCart();
  const bgColor = useColorModeValue("white", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const glowColor = useColorModeValue("blue.500", "blue.300");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const { colorMode } = useColorMode();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const filtered = products.filter(
      (product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchQuery]);

  // Get featured products (first 4)
  const featuredProducts = products.slice(0, 4);
  // Get best deals (products with highest discount)
  const bestDeals = [...products]
    .sort((a, b) => b.discount - a.discount)
    .slice(0, 4);

  // Get top 3 products by rating
  const topProducts = [...products]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 3);

  // Get products with highest discount
  const saleProducts = [...products]
    .filter((product) => product.discount > 30)
    .sort((a, b) => b.discount - a.discount)
    .slice(0, 3);

  // Get category icons
  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case "floral":
        return FaLeaf;
      case "oriental":
        return FaMagic;
      case "woody":
        return FaTree;
      case "fresh":
        return FaWind;
      case "gourmand":
        return FaCookie;
      case "aromatic":
        return FaSprayCan;
      default:
        return FaSprayCan;
    }
  };

  // Get category product count
  const getCategoryProductCount = (categoryId: string) => {
    return products.filter((product: Product) => product.category === categoryId).length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsSuccess(true);
      setEmail("");
    } catch (error) {
      console.error("Subscription error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        position="relative"
        height={{ base: "auto", md: "90vh" }}
        overflow="hidden"
        bg={bgColor}
      >
        <Hero />
      </Box>

      {/* Featured Products */}
      <Container maxW="1400px" py={20}>
        <VStack spacing={12} align="stretch">
          <HStack justify="space-between">
            <Heading
              size="xl"
              color={headingColor}
              textShadow="0 0 20px rgba(128, 90, 213, 0.3)"
            >
              Featured Collections
            </Heading>
            <Button
              as={RouterLink}
              to="/products"
              variant="ghost"
              rightIcon={<FiArrowRight />}
              color="purple.500"
              _hover={{ color: "purple.600" }}
            >
              View All
            </Button>
          </HStack>

          <MotionBox
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
              {featuredProducts.map((product) => (
                <MotionBox
                  key={product.id}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                >
                  <RouterLink to={`/product/${product.id}`}>
                    <Box
                      bg={bgColor}
                      borderRadius="xl"
                      overflow="hidden"
                      boxShadow="lg"
                      position="relative"
                      transition="all 0.3s"
                      cursor="pointer"
                      _hover={{
                        boxShadow: "0 0 30px rgba(128, 90, 213, 0.3)",
                        transform: "translateY(-5px)",
                      }}
                    >
                      <Image
                        src={product.image}
                        alt={product.name}
                        height="200px"
                        width="100%"
                        objectFit="cover"
                        transition="transform 0.3s"
                        _hover={{ transform: "scale(1.05)" }}
                      />
                      {product.discount > 0 && (
                        <Badge
                          colorScheme="purple"
                          position="absolute"
                          top={2}
                          right={2}
                          fontSize="sm"
                          px={2}
                          py={1}
                          borderRadius="full"
                        >
                          {product.discount}% OFF
                        </Badge>
                      )}
                      <VStack p={4} align="start" spacing={2}>
                        <Text
                          fontSize="lg"
                          fontWeight="bold"
                          noOfLines={1}
                          color={textColor}
                        >
                          {product.name}
                        </Text>
                        <HStack>
                          <Icon as={FiStar} color="yellow.400" />
                          <Text>{product.rating} / 5.0</Text>
                          <Text color="gray.500">({product.sold} sold)</Text>
                        </HStack>
                        <HStack justify="space-between" width="100%">
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="purple.500"
                          >
                            Rs. {product.price.toFixed(2)}
                          </Text>
                          {product.originalPrice && (
                            <Text
                              fontSize="sm"
                              textDecoration="line-through"
                              color="purple.300"
                            >
                              Rs. {product.originalPrice.toFixed(2)}
                            </Text>
                          )}
                        </HStack>
                        <Button
                          colorScheme="purple"
                          leftIcon={<FiShoppingBag />}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addToCart(product);
                          }}
                          width="100%"
                          borderRadius="full"
                          _hover={{
                            transform: "translateY(-1px)",
                            shadow: "md",
                          }}
                        >
                          Add to Cart
                        </Button>
                      </VStack>
                    </Box>
                  </RouterLink>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionBox>
        </VStack>
      </Container>

      {/* Features Section */}
      <Box py={12} bg={bgColor}>
        <Container maxW="1400px">
          <SimpleGrid columns={[1, 2, 4]} spacing={8}>
            <HStack spacing={4}>
              <Icon as={FiStar} w={8} h={8} color="purple.500" />
              <Box>
                <Text fontWeight="bold">Premium Quality</Text>
                <Text fontSize="sm" color="gray.600">
                  Authentic fragrances
                </Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <Icon as={FiShield} w={8} h={8} color="purple.500" />
              <Box>
                <Text fontWeight="bold">Authenticity Guaranteed</Text>
                <Text fontSize="sm" color="gray.600">
                  100% genuine products
                </Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <Icon as={FiTruck} w={8} h={8} color="purple.500" />
              <Box>
                <Text fontWeight="bold">Fast Delivery</Text>
                <Text fontSize="sm" color="gray.600">
                  Free shipping on orders
                </Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <Icon as={FiLock} w={8} h={8} color="purple.500" />
              <Box>
                <Text fontWeight="bold">Secure Payment</Text>
                <Text fontSize="sm" color="gray.600">
                  Safe checkout process
                </Text>
              </Box>
            </HStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Shop by Category Section */}
      <Box py={16}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center">
            <Heading size="2xl" color="purple.700" mb={4}>
              Shop by Category
            </Heading>
            <Text fontSize="xl" color="purple.500">
              Discover our curated collection of luxury fragrances
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {categories.map((category) => (
              <MotionBox
                key={category.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Box
                  as={RouterLink}
                  to={`/category/${category.id}`}
                  cursor="pointer"
                  _hover={{ textDecoration: "none" }}
                >
                  <Box
                    borderWidth="1px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg={bgColor}
                    shadow="md"
                    transition="all 0.3s"
                    _hover={{
                      shadow: "xl",
                      transform: "translateY(-4px)",
                    }}
                    height="100%"
                  >
                    <Box 
                      position="relative" 
                      height="280px" 
                      bg="purple.50"
                      p={8}
                    >
                      <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        height="100%"
                      >
                        <Icon
                          as={getCategoryIcon(category.id)}
                          w={20}
                          h={20}
                          color="purple.500"
                          mb={6}
                        />
                        <Heading size="lg" color="purple.700" mb={3}>
                          {category.name}
                        </Heading>
                        <Text 
                          color="gray.600" 
                          textAlign="center" 
                          mb={6}
                          fontSize="md"
                          lineHeight="1.6"
                          px={4}
                        >
                          {category.description}
                        </Text>
                        <HStack spacing={2} mt="auto">
                          <Text color="purple.500" fontWeight="bold" fontSize="md">
                            {getCategoryProductCount(category.id)} Fragrances
                          </Text>
                          <Icon as={FiArrowRight} color="purple.500" />
                        </HStack>
                      </Flex>
                    </Box>
                  </Box>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Newsletter Section */}
      <Box
        py={12}
        bg={colorMode === "light" ? "purple.900" : "gray.900"}
        color="white"
        position="relative"
        overflow="hidden"
      >
        {/* Decorative elements */}
        <Box
          position="absolute"
          top="-20%"
          right="-20%"
          width="60%"
          height="60%"
          bgGradient="radial(circle, purple.400 0%, transparent 70%)"
          opacity={0.1}
          filter="blur(40px)"
          transform="rotate(45deg)"
        />
        <Box
          position="absolute"
          bottom="-20%"
          left="-20%"
          width="60%"
          height="60%"
          bgGradient="radial(circle, purple.400 0%, transparent 70%)"
          opacity={0.1}
          filter="blur(40px)"
          transform="rotate(-45deg)"
        />

        <Container maxW="1400px">
          <Grid
            templateColumns={["1fr", "1fr 1fr"]}
            gap={8}
            alignItems="center"
          >
            <VStack align="start" spacing={4}>
              <Heading 
                size="lg"
                color="white"
                fontWeight="bold"
                letterSpacing="tight"
                textShadow="0 0 10px rgba(128, 90, 213, 0.5)"
              >
                Stay Updated!
              </Heading>
              <Text fontSize="lg" opacity={0.9}>
                Subscribe to our newsletter and get exclusive fragrance tips, 
                new perfume launches, and special offers delivered to your inbox.
              </Text>
              <VStack align="start" spacing={2}>
                <HStack>
                  <Icon as={FiCheck} color="purple.300" />
                  <Text>Latest fragrance trends and updates</Text>
                </HStack>
                <HStack>
                  <Icon as={FiCheck} color="purple.300" />
                  <Text>Special discounts on luxury perfumes</Text>
                </HStack>
                <HStack>
                  <Icon as={FiCheck} color="purple.300" />
                  <Text>Early access to new collections</Text>
                </HStack>
              </VStack>
            </VStack>
            <Box>
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <VStack spacing={4}>
                  <InputGroup>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      size="lg"
                      bg="white"
                      color="black"
                      border="none"
                      _hover={{ borderColor: "purple.400" }}
                      _focus={{ borderColor: "purple.400" }}
                    />
                    <InputRightElement width="auto">
                      <Button
                        style={{ marginTop: "8px" }}
                        type="submit"
                        colorScheme="purple"
                        isLoading={isSubmitting}
                        loadingText="Subscribing..."
                        disabled={isSuccess}
                        size="lg"
                        height="48px"
                        borderTopLeftRadius="0"
                        borderBottomLeftRadius="0"
                      >
                        {isSuccess ? (
                          <HStack>
                            <Icon as={FiCheck} />
                            <Text>Subscribed!</Text>
                          </HStack>
                        ) : (
                          "Subscribe"
                        )}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <Text fontSize="sm" opacity={0.8}>
                    By subscribing, you agree to our Privacy Policy and consent
                    to receive fragrance updates.
                  </Text>
                </VStack>
              </form>
            </Box>
          </Grid>
        </Container>

        {/* Success Alert */}
        {isSuccess && (
          <Box
            position="fixed"
            top={4}
            right={4}
            bg="purple.500"
            color="white"
            px={6}
            py={3}
            borderRadius="lg"
            boxShadow="lg"
            zIndex={1000}
            animation="slideIn 0.5s ease-out"
          >
            <HStack spacing={2}>
              <Icon as={FiCheck} />
              <Text>Successfully subscribed to our newsletter!</Text>
            </HStack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Home;
